import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon } from '../../components/Icon.tsx';
import { bookDemoUrl } from './contact.ts';

/**
 * Material 3 top app bar for the public site: text-button navigation, a tonal
 * sign-in and a filled demo CTA. No rules, no pills — separation is space and
 * tone, as everywhere else in the ecosystem.
 */
export function SiteHeader(): JSX.Element {
  const [open, setOpen] = useState(false);
  const close = (): void => setOpen(false);

  // Close the mobile overlay once the viewport grows past the md breakpoint,
  // matching wapplify.app's behavior (the desktop nav takes over there).
  useEffect(() => {
    if (!open) return;
    const onResize = (): void => {
      if (window.innerWidth >= 768) setOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [open]);

  // M3 navigation-drawer rows for the mobile sheet: full-width, fully rounded,
  // tonal on hover — no dividing rules.
  const sheetRow =
    'rounded-full px-4 py-3.5 text-lg font-medium text-foreground transition-colors hover:bg-[hsl(var(--surface-container))]';

  return (
    <>
      <header className="sticky top-0 z-40 bg-[hsl(var(--chrome))]/90 backdrop-blur-md">
        <div className="folio-wide flex h-16 items-center justify-between gap-3">
          <Link to="/" onClick={close} className="flex min-w-0 items-center gap-2.5">
            <img src="/icons/wapoffice-mark.svg" alt="" className="size-8" />
            <span className="head-dv text-xl leading-none">ވެޕްލިފައި އޮފީސް</span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            <Link to="/applications" className="btn-ghost">
              އެޕްލިކޭޝަންތައް
            </Link>
            <a href="/#pricing" className="btn-ghost">
              އަގުތައް
            </a>
            <a href="/#why" className="btn-ghost">
              ކީއްވެ ވެޕްލިފައި އޮފީސް
            </a>
            <Link to="/security" className="btn-ghost">
              ސެކިއުރިޓީ
            </Link>
          </nav>

          <div className="flex items-center gap-2">
            {/* the M3 buttons carry generous padding — pull it back on the
                narrowest phones so the bar never crowds the wordmark */}
            <Link to="/login" className="btn-ghost max-sm:px-3">
              ލޮގިން
            </Link>
            <a
              href={bookDemoUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary whitespace-nowrap max-sm:px-4"
            >
              <span className="md:hidden">ޑެމޯ</span>
              <span className="hidden md:inline">ޑެމޯއަކަށް ރިކުއެސްޓްކުރޭ</span>
            </a>

            {/* mobile menu toggle — an M3 icon button; opens the nav links in a
                full-screen sheet below. */}
            <button
              type="button"
              aria-label="މެނޫ"
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="-me-1.5 inline-flex size-10 items-center justify-center rounded-full text-foreground transition-colors hover:bg-[hsl(var(--surface-container))] md:hidden"
            >
              <Icon name={open ? 'close' : 'menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* mobile menu — full-screen sheet below the header, with large tappable
          M3 rows. Rendered as a header sibling: the header's backdrop-filter
          would otherwise trap this fixed element inside the 64px-tall bar. */}
      {open ? (
        <div className="fixed inset-x-0 bottom-0 top-16 z-30 bg-background md:hidden">
          <nav className="folio-wide flex flex-col gap-1 pt-3">
            <Link to="/applications" onClick={close} className={sheetRow}>
              އެޕްލިކޭޝަންތައް
            </Link>
            <a href="/#pricing" onClick={close} className={sheetRow}>
              އަގުތައް
            </a>
            <a href="/#why" onClick={close} className={sheetRow}>
              ކީއްވެ ވެޕްލިފައި އޮފީސް
            </a>
            <Link to="/security" onClick={close} className={sheetRow}>
              ސެކިއުރިޓީ
            </Link>
          </nav>
        </div>
      ) : null}
    </>
  );
}
